import {
  type EnumeratedLiteralType,
  enumeratedLiterals,
} from "../../constants";
import { type DbConversionsDateRow } from "../../protocols";

type Count = "30" | "60" | "90";
type AdditionalFields = `extraRev_to_${Count}` | `extraTxns_to_${Count}`;

type RelevantDbConversionsRow = Omit<DbConversionsDateRow, "date" | "adKey">;

export type MetricPointData = {
  [key in keyof RelevantDbConversionsRow as number extends RelevantDbConversionsRow[key]
    ? key
    : never]: RelevantDbConversionsRow[key];
} & Record<AdditionalFields, number>;

export type MetricPoint = RelevantDbConversionsRow &
  Record<AdditionalFields, number>;

export type MetricSeries = {
  [key: string]: MetricPoint;
};

export const MetricTypes = enumeratedLiterals(
  {
    decimal: "decimal",
    integer: "integer",
    percentage: "percentage",
    multiplier: "multiplier",
    /* Note: The "dollars" reference is an old value that was used before currency conversion
       was incorporated into the application.  It is stored in the database - so to avoid a DB
       migration at the present time we will simply maintain that older "dollars" reference, but
       just access it as "CURRENCY".

       Eventually, the "dollars" references should be replaced by "currency", because the "dollars"
       reference is not appropriate for all foreign currencies. */
    currency: "dollars",
  } as const,
  {
    getOrder: (a) => [
      a.DECIMAL,
      a.INTEGER,
      a.PERCENTAGE,
      a.MULTIPLIER,
      a.CURRENCY,
    ],
  },
);

export type MetricType = EnumeratedLiteralType<typeof MetricTypes>;

export type Metric = {
  id: string;
  name: string;
  format: MetricType;
  isPositiveChangeGood: boolean;
  description: string;
};

export type MetricFragment = Pick<Metric, "id" | "name" | "format">;
